import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// iter 115: transparent access-token refresh on 401. The backend issues a
// 15-minute `access_token` and a 7-day `refresh_token` cookie; without this
// interceptor the app appears to "randomly log the user out" every 15
// minutes as soon as the next API call hits an expired token. On 401 we
// call `/auth/refresh` (uses the still-valid refresh cookie), retry the
// original request, and only bubble the auth failure up to AuthContext
// when the refresh itself is rejected (real session end).
let refreshPromise = null;
let onAuthFailure = null;

export function setAuthFailureHandler(fn) {
  // AuthContext registers a handler so we can drop `user` state (→ redirect
  // to login) without importing the context here and creating a cycle.
  onAuthFailure = fn;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    // Skip refresh for the refresh call itself, the login flow, and any
    // request that already went through the retry loop once.
    const url = original?.url || "";
    const isAuthEndpoint = url.includes("/auth/refresh") || url.includes("/auth/login") || url.includes("/auth/logout");
    if (status !== 401 || isAuthEndpoint || original?._retried) {
      return Promise.reject(error);
    }
    original._retried = true;
    try {
      // Single in-flight refresh: dedupe concurrent 401s so we only ping
      // the refresh endpoint once regardless of how many requests raced.
      if (!refreshPromise) {
        refreshPromise = api.post("/auth/refresh").finally(() => {
          refreshPromise = null;
        });
      }
      await refreshPromise;
      return api(original);
    } catch (refreshErr) {
      if (typeof onAuthFailure === "function") onAuthFailure();
      return Promise.reject(refreshErr);
    }
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong, please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
